import React, { FC, useState, useEffect, useCallback } from "react";
import MainLayout from "../../Layouts/MainLayout";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import CommonForm from "./CommonForm";
import { toast } from "react-toastify";
import Label from "../atoms/Label";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableAgent } from "../molecules/SortableAgent";
import ListagentIcon from "../../static/svg/ListagentIcon";
import { service } from "../../../services/AxiosServices";
import SectionHeader from "./SectionHeader";
import WindowHeightComponent from "../../Layouts/WindowHeight";
//handles the creation and editing of a flow, including agent management, dynamic form updates, an

interface Agent {
  id: number;
  name: string;
}
interface FlowAgent extends Agent {
  originalId?: number;
}
interface FormState {
  values: {
    crew: string;
    flowName: string;
    flowDescription: string;
    outputType: number;
    fileOptions: string[];
    emails: string[];
    agentOrder: string[];
    agents: FlowAgent[];
    templateText: string;
  };
  clientErrors: Record<string, string>;
  serverError: string | null;
}

const CreateFlow: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { crewName } = location.state || {};
  const { flowId, id } = useParams();
  const [crewname, setCrewname] = useState('')
  const [crewDataLoaded, setCrewDataLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const flowID = Number(flowId);
  const isEdit = Boolean(flowID);
  const [formState, setFormState] = useState<FormState>({
    values: {
      crew: '',
      flowName: "",
      flowDescription: "",
      outputType: 1,
      fileOptions: [] as string[],
      agentOrder: [] as string[],
      emails: [] as string[],
      agents: [] as Agent[],
      templateText: "",
    },
    clientErrors: {} as Record<string, string>,
    serverError: null as string | null,
  });


  useEffect(() => {
    //Function to get single crew
    const singleCrew = async () => {
      try {
        setLoading(true);
        const response = await service<any>('/api/get-all-crew', 'post', {
          project_id: localStorage.getItem('project_id')
        });
        const formattedData = response.data.crews?.find((ele: any) => ele.id == id)?.crew_name;
        if (formattedData) {
          setCrewname(formattedData);
          setFormState(prev => ({
            ...prev,
            values: {
              ...prev.values,
              crew: formattedData
            }
          }));
        }
      } catch (err) {
        console.error('Error fetching crew name:', err);
      } finally {
        setCrewDataLoaded(true);
        setLoading(false);
      }
    };
    singleCrew();
  }, [id]);



  useEffect(() => {
    if (!crewDataLoaded) return;
    const fetchAgent = async () => {
      try {
        setLoading(true);

        const response = await service<Record<string, any>>(
          "api/get-all-agents/",
          "post",
          { crew_id: id }
        );
        const fetchedAgents: Agent[] = response.data.agents.map(
          (agent: any) => ({
            id: agent.id,
            name: agent.agent_name || `Agent`,
          })
        );

        setAgents(fetchedAgents);
      } catch (err) {
        console.log("Error fetching agents:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAgent();
    if (isEdit) {
      const fetchFlows = async () => {
        setLoading(true);
        try {
          const response = await service<Record<string, any>>(
            "/api/get-flow-with-id",
            "post",
            { crew_id: id, flow_id: flowID }
          );


          const flowData = response?.data?.data;

          // Map agent order data to an array with unique IDs
          const mappedAgents = flowData?.agent_order?.map((agent: any) => ({
            id: Date.now() + Math.random(),
            name: agent?.name,
            originalId: parseInt(agent?.id),
          }));

          const agentOrderArray = flowData?.agent_order?.map(
            (agent: any) => agent?.id
          );
          // Parse emails from the response if available
          let parsedEmails: string[] = [];
          try {
            if (flowData?.output_type?.emails === "[]") {
              parsedEmails = JSON.parse(flowData?.output_type?.emails);
            } else if (flowData?.output_type?.emails) {
              parsedEmails = flowData?.output_type?.emails;
            }
          } catch (e) {
            console.log("Error parsing emails:", e);
            parsedEmails = [];
          }

          setFormState((prev) => ({
            ...prev,
            values: {
              crew: crewname || "",
              flowName: flowData?.flow_name,
              flowDescription: flowData?.flow_description,
              outputType: flowData?.output_type?.type,
              fileOptions: Array.isArray(flowData?.output_type?.file_options)
                ? flowData?.output_type?.file_options
                : [],
              emails: parsedEmails,
              agentOrder: agentOrderArray,
              agents: mappedAgents,
              templateText: flowData?.output_type?.template_text || "",
            },
            clientErrors: {},
            serverError: null,
          }));
        } catch (err: any) {
          console.log("Error in fetchFlows:", err);
          setFormState((prev) => ({
            ...prev,
            serverError: err.message || "Error loading flow data",
          }));
        } finally {
          setLoading(false);
        }
      };
      Promise.all([fetchAgent(), fetchFlows()]);
    }


  }, [crewDataLoaded, id, isEdit, flowID, crewname]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  //client-side validations
  const validations = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    if (!values.crew) errors.crew = "Crew is required";
    if (!values.flowName) errors.flowName = "Flow name is required";
    if (!values.flowDescription)
      errors.flowDescription = "Flow description is required";

    if (![1, 2, 3].includes(Number(values.outputType))) {
      errors.outputType = "Invalid output type. Must be 1, 2, or 3";
    }

    if (
      values.outputType === 1 &&
      (!values.fileOptions || values.fileOptions.length === 0)
    ) {
      errors.fileFormats =
        "At least one file format is required for file output type";
    }

    if (values.outputType === 2) {
      if (!values.templateText) {
        errors.templateText =
          "Email template is required for email output type";
      }
      if (!values.emails || values.emails.length === 0) {
        errors.emails =
          "At least one email address is required for email output type";
      }
    }

    return errors;
  };

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value, type } = e.target;

      if (type === "checkbox") {
        const checkbox = e.target as HTMLInputElement;

        setFormState((prev) => {
          // If checked, add value to fileOptions array; otherwise, remove it
          const updatedFileOptions = checkbox.checked
            ? [...prev.values.fileOptions, value]
            : prev.values.fileOptions.filter((opt) => opt !== value);

          return {
            ...prev,
            values: { ...prev.values, fileOptions: updatedFileOptions },
            clientErrors: { ...prev.clientErrors, [name]: "" },
          };
        });
      } else if (name === "outputType") {
        const numericValue = parseInt(value);

        setFormState((prev) => {
          const newState = {
            ...prev,
            values: { ...prev.values, [name]: numericValue },
            clientErrors: { ...prev.clientErrors, [name]: "" },
            serverError: null,
          };

          return newState;
        });
      } else {
        setFormState((prev) => ({
          ...prev,
          values: { ...prev.values, [name]: value },
          clientErrors: { ...prev.clientErrors, [name]: "" },
          serverError: null,
        }));
      }
    },
    []
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setFormState((prev) => {
        // Find the indices of the dragged item and its target position
        const oldIndex = prev.values.agents.findIndex(
          (agent) => agent.id === active.id
        );
        const newIndex = prev.values.agents.findIndex(
          (agent) => agent.id === over.id
        );
        // Reorder agents using arrayMove (assumed utility function)
        const newAgents = arrayMove(prev.values.agents, oldIndex, newIndex);
        // Update agentOrder to maintain a valid sequence
        const newAgentOrder = newAgents.map((agent) =>
          agent.originalId ? agent.originalId.toString() : agent.id.toString()
        );

        return {
          ...prev,
          values: {
            ...prev.values,
            agents: newAgents,
            agentOrder: newAgentOrder,
          },
        };
      });
    }
  };

  const handleAgentDrop = (agentId: number) => {
    // Find the agent in the available agents list
    const agent = agents.find((a: Agent) => a.id === agentId);
    if (agent) {
      const uniqueId = Date.now() + Math.random();
      const newAgent = {
        ...agent,
        id: uniqueId,
        originalId: agent.id,
      };

      setFormState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          agents: [...prev.values.agents, newAgent],

          agentOrder: [...prev.values.agentOrder, agent.id.toString()],
        },
      }));
    }
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const errors = validations(formState.values);
      if (Object.keys(errors).length === 0) {
        try {

          const flowData = {
            ...(isEdit && { id: flowID }),
            flow_name: formState.values.flowName,
            flow_description: formState.values.flowDescription,
            crew: id,
            agent_order: formState.values.agentOrder,
            output_type: Number(formState.values.outputType),

            ...(formState.values.outputType === 1 && {
              file_options: formState.values.fileOptions,
              template_text: "Initial email template.",
            }),
            emails: formState.values.emails,
            ...(formState.values.outputType === 2 && {
              template_text: formState.values.templateText,
              file_options: [],
            }),
            ...(formState.values.outputType === 3 && {
              file_options: [],
              template_text: "",
              emails: [],
            }),
          };

          const response = await service<Record<string, any>>(
            "/api/flow",
            "post",
            flowData
          );
          toast.success(
            `Flow ${isEdit ? "updated" : "created"} successfully!`,
            { autoClose: 2000 }
          );
          navigate(`/flows/${id}`, { state: { crewName } });
          setFormState({
            values: {
              crew: crewname || "",
              flowName: "",
              flowDescription: "",
              outputType: 1,
              fileOptions: [] as string[],
              agentOrder: [] as string[],
              emails: [] as string[],
              agents: [] as Agent[],
              templateText: "",
            },
            clientErrors: {} as Record<string, string>,
            serverError: null as string | null,
          });
        } catch (err: any) {
          console.log(err);
          const errorMessage =
            err.response?.data?.error || "Something went wrong. Please try again..";
          setFormState((prev) => ({ ...prev, serverError: errorMessage }));
        }
      } else {
        setFormState((prev) => ({ ...prev, clientErrors: errors }));
      }
    },
    [formState.values, navigate]
  );
  const fields = [
    {
      name: "crew",
      type: "text",
      label: "Crew",
      disabled: true,
      required: true,
    },
    { name: "flowName", type: "text", label: "Flow name", required: true },
    {
      name: "flowDescription",
      type: "textarea",
      label: "Flow description",
      rows: 5,
      cols: 50,
      required: true,
    },
  ];

  const outputTypeFields = [
    {
      name: "outputType",
      type: "radio",
      label: "Output type:",
      options: [
        { value: 1, label: "File type" },
        { value: 2, label: "Email type" },
        { value: 3, label: "Custom integration" },
      ],
    },
  ];

  const fileFormatFields = [
    {
      name: "fileFormats",
      type: "checkbox",
      label: "File formats",
      options: [
        { value: "pdf", label: "PDF" },
        { value: "csv", label: "CSV" },
        { value: "excel", label: "Excel" },
        { value: "word", label: "Word" },
      ],
    },
  ];

  const clientErrorsArray = Object.entries(formState.clientErrors).reduce(
    (acc: string[], [field, error]) => {
      if (error) {
        acc.push(`${error}`);
      }
      return acc;
    },
    []
  );
  const handleCancel = () =>
    navigate(`/flows/${id}`, { state: { crewName } });
  return (
    <MainLayout>

      <SectionHeader title={isEdit ? "Edit flow" : "Create flow"}></SectionHeader>
      <WindowHeightComponent className={""}>
        <div className="row m-0 p-3">
          <div className="col-9 ps-0 pe-4">
            {formState.serverError && (
              <div className="alert alert-danger">{formState.serverError}</div>
            )}
            {clientErrorsArray.length > 0 && (
              <div className="alert alert-danger">
                <ul>
                  {clientErrorsArray.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            <CommonForm
              fields={fields}
              values={formState.values}
              onChange={handleInputChange}
              onCancel={isEdit ? handleCancel : undefined}
              isEdit={isEdit}
              onSubmit={handleSubmit}
            >
              <div className="mb-3">
                <Label>Drop agents below in order</Label>
                <div
                  className="list-group dropzone p-3"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const agentId = parseInt(e.dataTransfer.getData("agentId"));
                    handleAgentDrop(agentId);
                  }}
                >
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={formState.values.agents.map((agent) => agent.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {formState.values.agents.map((agent) => (
                        <SortableAgent key={agent.id} agent={agent} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              </div>

              <div className="mb-4">
                <h5>Output configuration</h5>
                {outputTypeFields.map((field) => (
                  <div key={field.name} className="mb-3">
                    <label className="d-block mb-2">{field.label}</label>
                    <div className="d-flex gap-3">
                      {field.options.map((option) => (
                        <div key={option.value} className="form-check">
                          <input
                            type="radio"
                            name={field.name}
                            value={option.value}
                            checked={
                              formState.values.outputType === option.value
                            }
                            onChange={handleInputChange}
                            className="form-check-input"
                          />
                          <label className="form-check-label">
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {formState.values.outputType === 1 && (
                  <div className="mb-3">
                    {fileFormatFields.map((field) => (
                      <div key={field.name}>
                        <label className="d-block mb-2">{field.label}</label>
                        <div className="d-flex gap-3">
                          {field.options.map((option) => (
                            <div key={option.value} className="form-check">
                              <input
                                type="checkbox"
                                name={field.name}
                                value={option.value}
                                checked={formState.values.fileOptions.includes(
                                  option.value
                                )}
                                onChange={handleInputChange}
                                className="form-check-input"
                              />
                              <label className="form-check-label">
                                {option.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {formState.values.outputType === 2 && (
                  <div className="mb-3">
                    <label className="form-label">Email Template:</label>
                    <textarea
                      name="templateText"
                      value={formState.values.templateText}
                      onChange={handleInputChange}
                      className="form-control"
                      rows={10}
                      cols={5}
                    ></textarea>

                    <br />
                    <label className="form-label  LabelText mb-1">
                      Emails (comma-separated):
                    </label>
                    <input
                      type="text"
                      name="emails"
                      value={formState.values.emails}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Enter emails separated by commas"
                    />
                  </div>
                )}
              </div>
            </CommonForm>

          </div>
          <div className="col-3 border-left ps-3">
            <div className="mb-4">
              <h4>List of agents</h4>
              {agents.map((agent: any) => (
                <div className="ps-3 mt-3">
                  <div className="d-flex bg-white p-3 w-100 border mb-3 cursor-pointer draggable">
                    <span className="me-3">
                      <ListagentIcon />
                    </span>
                    <div
                      key={agent.id}
                      className=""
                      draggable
                      onDragStart={(e) =>
                        e.dataTransfer.setData("agentId", agent.id)
                      }
                    >
                      {agent.name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </WindowHeightComponent>
    </MainLayout>
  );
};

export default CreateFlow;